import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BaseApiService } from 'src/app/shared/services/base-api.service';
import { CreateFundBookingInput, FundTypeEnum, EFundStatus, OpportunityItemDto, UpdateOpportunityTermsInput, FundingDetailsResponse, SubmitFundingDetailsRequest, InitFund, FundApplicationDto, FirstStepData, SignAgreementRequest, RejectBorrowerFinancingAgreementRequest } from '../../data-models/opportunity.data.models';
import { GeneralResponse, PaginatedList } from '../../data-models/shared.data-models';
import { GetMyInvestmentsOutputDto } from '../../data-models/investment.data.models';
import { environment } from 'src/environments/environment';
import { UserService } from '../../common-services/user.services';
import { LanguageService } from 'src/app/shared/services/language.service';
import { AttachmentService } from '../attachments/attachment.service';

export interface AgencyAuthorizationLetterUploadRequest {
  fundId?: string;
  storedFileId?: string;
  contentType?: string;
  fileExtension?: string;
}

export interface AgencyAuthorizationLetterUploadResponse {
  storedFileId: string;
  fileUrl?: string | null;
  previewPath: string;
}

// Backward-compatible aliases used by step-1 compliance component.
export interface AttachmentUploadRequest {
  storedFileId?: string;
  contentType?: string;
  fileExtension?: string;
  fundId?: string;
  parentFolderName?: string;
  attachmentTypeName?: string;
}

export interface AttachmentUploadResponse {
  storedFileId: string;
  url?: string | null;
}

export interface FundAttachmentUploadRequest {
  attachmentType: number | string;
  storedFileId?: string;
  contentType?: string;
  fileExtension?: string;
  /** Optional: 1 = Submitted (backend FundAttachmentSubmissionStatus). */
  submissionStatus?: number;
}

/** One uploaded file for a borrower attachment type (API: `files`). */
export interface FundAttachmentFile {
  storedFileId: string;
  previewUrl: string;
  fileUrl: string;
}

export interface FundAttachmentItem {
  attachmentType: number;
  attachmentTypeNameAr: string;
  attachmentTypeNameEn: string;
  isUploaded: boolean;
  /** First file (legacy / convenience). */
  storedFileId: string;
  previewUrl: string;
  fileUrl: string;
  /** All files for this attachment type. */
  files: FundAttachmentFile[];
}

export interface FundAttachmentStatusResponse {
  items: FundAttachmentItem[];
  canProceedToNextStep: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FundDataService {

 // private controllerUrl: string = `${environment.appConfig.gatewayServiceBaseUrl}/api/1/Fund/`;
  
  private readonly controllerUrl = `${environment.appConfig.gatewayServiceBaseUrl}/api/portal/FundBorrower/`;

  constructor(
    private baseApi: BaseApiService<any>,
    private httpClient: HttpClient,
    private userService: UserService,
    private languageService: LanguageService,
    private attachmentService: AttachmentService,
  ) { }

  GetMyInvestmentsSummary(): Observable<any> {
    return this.baseApi.Get(`${this.controllerUrl}GetMyInvestmentsSummary`);
  }
  GetMyInvestments(fundType?: FundTypeEnum | null): Observable<GeneralResponse<PaginatedList<GetMyInvestmentsOutputDto>>> {
    let fundTypeParam = fundType ? `?fundType=${fundType}` : '';
    return this.baseApi.Get(`${this.controllerUrl}GetMyInvestments${fundTypeParam}`);
  }
  RecoverMyInvestmentRequest(opportunityId: string, recoverUnitsCount: number, investorSignature: string): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}CreateRecoverMyInvestmentRequest`, { opportunityId, recoverUnitsCount, investorSignature });
  }
  GetAllOpportunities(fundStatusId?: EFundStatus | null,fundTypeId?:FundTypeEnum , pageIndex?: number, pageSize?: number): Observable<GeneralResponse<PaginatedList<OpportunityItemDto>>> {
    let fundTypeParam = fundStatusId != undefined ? `&fundStatusId=${fundStatusId}` : '';
    let fundTypeIdParam = fundTypeId != undefined ? `&fundTypeId=${fundTypeId}` : '';
    return this.baseApi.Get(`${this.controllerUrl}GetAllOpportunities?pageIndex=${pageIndex}&pageSize=${pageSize}${fundTypeParam}${fundTypeIdParam}`);
  }
  GetOpportunityDetails(opportunityId: string): Observable<any> {
    return this.baseApi.Get(`${this.controllerUrl}GetOpportunityDetails?opportunityId=${opportunityId}`);
  }
  GetOpportunityBookingSummary(opportunityId: string): Observable<any> {
    return this.baseApi.Get(`${this.controllerUrl}GetOpportunityBookingSummary?opportunityId=${opportunityId}`);
  }
  GetPrebookingFundReports(fundId: string): Observable<any> {
    return this.baseApi.Get(`${this.controllerUrl}GetPrebookingFundReports?fundId=${fundId}`);
  }
  UpdateOpportunityTerms(body: UpdateOpportunityTermsInput): Observable<any> {
    return this.baseApi.Put(`${this.controllerUrl}UpdateOpportunityTerms`, body);
  }
  CreateFundBookingRequest(body: CreateFundBookingInput): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}CreateFundBookingRequest`, body);
  }
  GetInvestmentFundReports(investmentFundId: string) {
    return this.baseApi.Get(`${this.controllerUrl}InvestmentFundReports?investmentFundId=${investmentFundId}`);

  }
  GetTermsAndConditions(fundId: string) {
    return this.baseApi.Get(`${this.controllerUrl}GetTermsAndConditions?fundId=${fundId}`);
  }
  GetFundingDetails(fundId:string): Observable<GeneralResponse<FundingDetailsResponse>> {
    return this.baseApi.Get(`${this.controllerUrl}GetFundingDetails?fundId=${fundId}`);
  }
  SubmitFundingDetails(body: SubmitFundingDetailsRequest): Observable<GeneralResponse<FundingDetailsResponse>> {
    return this.baseApi.Post(`${this.controllerUrl}SubmitFundingDetails`, body);
  }
  /**
   * Load first-step draft by fund id.
   * Backend supports `fundId` query for borrower flow.
   */
  GetFirstStep(fundId?: string): Observable<GeneralResponse<FirstStepData>> {
    const q = fundId ? `?fundId=${encodeURIComponent(fundId)}` : '';
    return this.baseApi.Get(`${this.controllerUrl}GetFirstStep${q}`);
  }
  UploadAgencyAuthorizationLetter(
    body: AgencyAuthorizationLetterUploadRequest
  ): Observable<GeneralResponse<AgencyAuthorizationLetterUploadResponse>> {
    return this.baseApi.Post(`${this.controllerUrl}UploadAgencyAuthorizationLetter`, body);
  }

  UploadFundBorrowerAttachment(
    fundId: string,
    body: FundAttachmentUploadRequest
  ): Observable<GeneralResponse<FundAttachmentItem>> {
    const q = `?fundId=${encodeURIComponent(fundId)}`;
    return this.baseApi.Post(`${this.controllerUrl}UploadFundBorrowerAttachment${q}`, body);
  }

  GetFundBorrowerAttachments(fundId: string): Observable<GeneralResponse<FundAttachmentStatusResponse>> {
    const q = `?fundId=${encodeURIComponent(fundId)}`;
    return this.baseApi.Get(`${this.controllerUrl}GetFundBorrowerAttachments${q}`);
  }

  DeleteFundBorrowerAttachment(
    fundId: string,
    storedFileId: string
  ): Observable<GeneralResponse<FundAttachmentItem>> {
    const q = `?fundId=${encodeURIComponent(fundId)}&storedFileId=${encodeURIComponent(storedFileId)}`;
    return this.baseApi.Delete(`${this.controllerUrl}DeleteFundBorrowerAttachment${q}`);
  }

  /** Backward-compatible upload API used by existing step-1 UI. */
  UploadAttachment(body: AttachmentUploadRequest): Observable<GeneralResponse<AttachmentUploadResponse>> {
    const request: AgencyAuthorizationLetterUploadRequest = {
      fundId: body.fundId ?? '',
      storedFileId: body.storedFileId,
      contentType: body.contentType,
      fileExtension: body.fileExtension,
    };

    return this.UploadAgencyAuthorizationLetter(request).pipe(
      map((res) => {
        if (!res?.resource) {
          return res as unknown as GeneralResponse<AttachmentUploadResponse>;
        }

        return {
          ...res,
          resource: {
            storedFileId: res.resource.storedFileId,
            url: res.resource.fileUrl ?? null,
          } as AttachmentUploadResponse,
        };
      })
    );
  }
  DeleteAgencyAuthorizationLetter(storedFileId: string): Observable<GeneralResponse<boolean>> {
    const q = `?storedFileId=${encodeURIComponent(storedFileId)}`;
    return this.baseApi.Delete(`${this.controllerUrl}DeleteAgencyAuthorizationLetter${q}`);
  }
  SubmitFirstStep(body: FirstStepData): Observable<GeneralResponse<FirstStepData>> {
    return this.baseApi.Post(`${this.controllerUrl}SubmitFirstStep`, body);
  }
  CreateInitialFund(): Observable<GeneralResponse<InitFund>> {
    return this.baseApi.Post(`${this.controllerUrl}CreateInitialFund`, {});
  }


  SaveDraftFirstStep(body: FirstStepData): Observable<GeneralResponse<InitFund>> {
    return this.baseApi.Post(`${this.controllerUrl}SaveDraftFirstStep`, body);
  }

  /** Persist company documents step draft (expected by FundBorrower API alongside SaveDraftFirstStep). */
  SaveDraftSecondStep(fundId: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}SaveDraftSecondStep`, { fundId });
  }

  /** Persist funding details draft without finalizing the wizard step. */
  SaveDraftFundingDetails(body: SubmitFundingDetailsRequest): Observable<GeneralResponse<FundingDetailsResponse>> {
    return this.baseApi.Post(`${this.controllerUrl}SaveDraftFundingDetails`, body);
  }

FinalReview(fundId: string) {
  return this.baseApi.Post(`${this.controllerUrl}FinalReview`, { fundId });
}

getAll(
  pageIndex: number = 1,
  pageSize: number = 10
): Observable<GeneralResponse<PaginatedList<FundApplicationDto>>> {
  const body = {
    pageIndex: pageIndex,
    pageSize: pageSize
  };

  return this.baseApi.Post(
    `${this.controllerUrl}getFunds`,
    body
  );
}
getAgreementDetails(applicationId: string): Observable<GeneralResponse<any>> {
  return this.baseApi.Get(`${this.controllerUrl}GetAgreementDetails?applicationId=${applicationId}`);
}

  private getBlobWithFilename(url: string): Observable<{ blob: Blob; filename: string }> {
    let headers = new HttpHeaders();
    const acceptLanguage = this.languageService.currentLanguage == 'ar' ? 'ar-SA' : 'en-US';
    headers = headers.set('Accept-Language', acceptLanguage);

    const token = this.userService.currentUserCredantials?.token;
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.httpClient.get(url, { 
      responseType: 'blob', 
      observe: 'response',
      headers: headers
    }).pipe(
      map((response: any) => {
        // Extract filename from Content-Disposition header if available
        const contentDisposition = response.headers?.get('content-disposition');
        let filename = 'agreement.pdf';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
            // Decode URI encoded filename
            try {
              filename = decodeURIComponent(filename);
            } catch (e) {
              // If decoding fails, use the original filename
            }
          }
        }
        return { blob: response.body as Blob, filename: filename };
      })
    );
  }

  getFileAsBlob(documentId: string): Observable<Blob> {
    return this.getBlobWithFilename(`${this.controllerUrl}DownloadAgreement/${documentId}`).pipe(
      map(result => {
        (result.blob as any).filename = result.filename;
        return result.blob;
      })
    );
  }

  viewAgreement(applicationId: string): Observable<{ blob: Blob; filename: string }> {
    return this.getBlobWithFilename(`${this.controllerUrl}DownloadAgreement/${applicationId}`);
  }

  /**
   * Signed borrower agreement PDF persisted as a stored file (GetAgreementDetails.signedAgreementCopyId).
   * Same route pattern as other fund attachment downloads.
   */
  viewSignedAgreementFundAttachment(storedFileId: string): Observable<{ blob: Blob; filename: string }> {
    return from(
      this.attachmentService.getViewUrl(storedFileId, true).then(async (url) => {
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`Could not download the file (${res.status}).`);
        }
        const blob = await res.blob();
        return { blob, filename: 'agreement.pdf' };
      })
    );
  }

  /** Fetch uploaded agency letter via authenticated preview path or stored-file id. */
  viewAgencyAuthorizationLetter(previewPath: string): Observable<{ blob: Blob; filename: string }> {
    const idMatch = previewPath?.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
    if (idMatch) {
      return this.viewSignedAgreementFundAttachment(idMatch[0]);
    }
    const base = environment.appConfig.gatewayServiceBaseUrl?.replace(/\/$/, '') || '';
    const rel = previewPath.startsWith('/') ? previewPath : `/${previewPath}`;
    return this.getBlobWithFilename(`${base}${rel}`);
  }
  viewFile(previewPath: string): Observable<{ blob: Blob; filename: string }> {
    return this.getBlobWithFilename(`${previewPath}`);
  }

  signAgreement(agreementId: string, otp: string): Observable<GeneralResponse<boolean>> {
    const request: SignAgreementRequest = {
      fundId: agreementId,
      otp: otp
    };

    return this.baseApi.Post(`${this.controllerUrl}SignAgreement`, request);
  }

  rejectBorrowerFinancingAgreement(
    agreementId: string,
    reason: string
  ): Observable<GeneralResponse<boolean>> {
    const body: RejectBorrowerFinancingAgreementRequest = {
      fundId: agreementId,
      reason: reason.trim(),
    };

    return this.baseApi.Post(`${this.controllerUrl}RejectBorrowerFinancingAgreement`, body);
  }

  getBorrowerFinancingContractPortal(fundId: string): Observable<GeneralResponse<any>> {
    const q = `?fundId=${encodeURIComponent(fundId)}`;
    return this.baseApi.Get(`${this.controllerUrl}GetBorrowerFinancingContractPortal${q}`);
  }

  downloadBorrowerFinancingContractBlob(fundId: string): Observable<{ blob: Blob; filename: string }> {
    const url = `${this.controllerUrl}DownloadBorrowerFinancingContract?fundId=${encodeURIComponent(fundId)}`;
    return this.getBlobWithFilename(url);
  }

  signBorrowerFinancingContract(fundId: string, otp: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}SignBorrowerFinancingContract`, { fundId, otp });
  }

  generateBorrowerSigningOtp(fundId: string): Observable<GeneralResponse<{
    mobileLast4?: string;
    MobileLast4?: string;
    message?: string;
    devOtp?: string;
    DevOtp?: string;
  }>> {
    return this.baseApi.Post(`${this.controllerUrl}GenerateBorrowerSigningOtp`, { fundId });
  }
}
